/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { AppThunk } from "store/store";
import { API_ENDPOINTS } from "config/apiConfig";
import { axiosFormDataInstance } from "config/axiosConfig";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { hideLoading, showLoading } from "./loadingSlice";

interface UploadState {
  uploadImageData: any;
  uploadImageMultipleData: any;
  uploadPdfData: any;
  loading: boolean;
  error: string | null;
}

const initialState: UploadState = {
  uploadImageData: null,
  uploadImageMultipleData: null,
  uploadPdfData: null,
  loading: false,
  error: null,
};

export const uploadSlice = createSlice({
  name: "upload",
  initialState,
  reducers: {
    uploadImageStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    uploadImageSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.uploadImageData = action.payload;
      state.error = null;
    },
    uploadImageFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.uploadImageData = null;
      state.error = action.payload;
    },
    uploadImageMultipleStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    uploadImageMultipleSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.uploadImageMultipleData = action.payload;
      state.error = null;
    },
    uploadImageMultipleFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.uploadImageMultipleData = null;
      state.error = action.payload;
    },
    uploadPdfStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    uploadPdfSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.uploadPdfData = action.payload;
      state.error = null;
    },
    uploadPdfFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.uploadPdfData = null;
      state.error = action.payload;
    },
    uploadCleanData: (state) => {
      state.uploadImageData = null;
      state.uploadImageMultipleData = null;
      state.uploadPdfData = null;
      state.error = null;
    },
  },
});

export const {
  uploadImageStart,
  uploadImageSuccess,
  uploadImageFailure,
  uploadImageMultipleStart,
  uploadImageMultipleSuccess,
  uploadImageMultipleFailure,
  uploadPdfStart,
  uploadPdfSuccess,
  uploadPdfFailure,
  uploadCleanData,
} = uploadSlice.actions;

export const uploadImage =
  (formData: FormData, path: string, signal: AbortSignal): AppThunk =>
  async (dispatch: any) => {
    dispatch(uploadImageStart());
    try {
      const response = await axiosFormDataInstance.post(
        `${API_ENDPOINTS.UPLOAD_FILE}?path_store=${path}`,
        formData,
        {
          signal,
        },
      );
      dispatch(uploadImageSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(uploadImageFailure(error.message || "Something went wrong"));
      }
    }
  };

export const uploadImageMultiple =
  (formData: FormData, path: string, signal: AbortSignal): AppThunk =>
  async (dispatch: any) => {
    dispatch(uploadImageMultipleStart());
    dispatch(showLoading());
    try {
      const response = await axiosFormDataInstance.post(
        `${API_ENDPOINTS.UPLOAD_FILE_MULTIPLE}?path_store=${path}`,
        formData,
        {
          signal,
        },
      );
      dispatch(uploadImageMultipleSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(
          uploadImageMultipleFailure(error.message || "Something went wrong"),
        );
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export const uploadPdf =
  (formData: FormData, path: string, signal: AbortSignal): AppThunk =>
  async (dispatch: any) => {
    dispatch(uploadPdfStart());
    try {
      const response = await axiosFormDataInstance.post(
        `${API_ENDPOINTS.UPLOAD_FILE}?path_store=${path}`,
        formData,
        {
          signal,
        },
      );
      dispatch(uploadPdfSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(uploadPdfFailure(error.message || "Something went wrong"));
      }
    }
  };

export default uploadSlice.reducer;
